import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { CheckCircle2, X } from 'lucide-react';

// Fake data for social proof - randomized names and locations
const recentPurchases = [
  { name: "Sarah M.", location: "Texas", plan: "Lifetime" },
  { name: "Jason K.", location: "California", plan: "Monthly" },
  { name: "Michael R.", location: "Florida", plan: "Lifetime" },
  { name: "Emily T.", location: "New York", plan: "Lifetime" },
  { name: "David L.", location: "Arizona", plan: "Monthly" },
  { name: "Amanda S.", location: "Colorado", plan: "Lifetime" },
  { name: "Chris P.", location: "Washington", plan: "Monthly" },
  { name: "Jennifer W.", location: "Georgia", plan: "Lifetime" },
  { name: "Ryan B.", location: "Illinois", plan: "Monthly" },
  { name: "Ashley H.", location: "Ohio", plan: "Lifetime" },
  { name: "Brandon M.", location: "North Carolina", plan: "Lifetime" },
  { name: "Nicole C.", location: "Michigan", plan: "Monthly" },
  { name: "Kevin T.", location: "Pennsylvania", plan: "Lifetime" },
  { name: "Stephanie R.", location: "Virginia", plan: "Monthly" },
  { name: "Justin A.", location: "Tennessee", plan: "Lifetime" },
];

const timeAgo = () => {
  const times = ["just now", "2 minutes ago", "5 minutes ago", "8 minutes ago", "12 minutes ago"];
  return times[Math.floor(Math.random() * times.length)];
};

export const SocialProofToast = () => {
  const [currentPurchase, setCurrentPurchase] = useState<typeof recentPurchases[0] | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Don't show if user dismissed
    if (isDismissed) return;

    // Initial delay before first toast (8-15 seconds)
    const initialDelay = Math.random() * 7000 + 8000;
    
    const showToast = () => {
      const randomPurchase = recentPurchases[Math.floor(Math.random() * recentPurchases.length)];
      setCurrentPurchase(randomPurchase);
      setIsVisible(true);
      
      // Hide after 5 seconds
      setTimeout(() => {
        setIsVisible(false);
      }, 5000);
    };

    // Show first toast after initial delay
    const initialTimeout = setTimeout(showToast, initialDelay);

    // Then show periodically (every 25-45 seconds)
    const interval = setInterval(() => {
      if (!isDismissed) {
        showToast();
      }
    }, Math.random() * 20000 + 25000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [isDismissed]);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
  };

  return (
    <AnimatePresence>
      {isVisible && currentPurchase && (
        <motion.div
          initial={{ opacity: 0, x: -100, y: 20 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: -100 }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
          className="fixed bottom-20 sm:bottom-6 left-4 z-50 max-w-[300px]"
        >
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 p-4 flex items-start gap-3">
            {/* Green check icon */}
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900">
                {currentPurchase.name} from {currentPurchase.location}
              </p>
              <p className="text-xs text-slate-600">
                just enrolled in {currentPurchase.plan} Access
              </p>
              <p className="text-xs text-slate-400 mt-1">
                {timeAgo()}
              </p>
            </div>

            {/* Dismiss button */}
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-slate-100 rounded-full transition-colors flex-shrink-0"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>
          
          {/* Subtle verified badge */}
          <div className="flex items-center gap-1 mt-2 ml-1">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            <span className="text-xs text-slate-400">Verified purchase</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SocialProofToast;
